import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './RichTextEditor.css';
import { isAllowedUrl } from './richTextConfig';

// SECURITY — CVE-2025-15056 (quill@2.0.3, no upstream patch as of 2026-06-14):
// XSS via quill's HTML export feature. This file is safe: all output paths
// use getContents() → JSON.stringify (Delta), never HTML.
//
// The read-only render path (display side) is `RichTextViewer` (issue #997):
//   import DOMPurify from 'dompurify';
//   const html = DOMPurify.sanitize(quill.getSemanticHTML(), DOMPURIFY_CONFIG);
//   <div dangerouslySetInnerHTML={{ __html: html }} />
//
// NEVER pass getSemanticHTML() / root.innerHTML directly to dangerouslySetInnerHTML.
// See: documents/specs/rich-text-spec.md — DOMPurify is installed (v3.4.10).

const COMPACT_TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'code'],
  ['clean'],
];

const STANDARD_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  ['code', 'blockquote', 'code-block'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['link'],
  [{ color: [] }, { background: [] }],
  ['clean'],
];

const ALLOWED_FORMATS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'link',
  'color',
  'background',
  'size',
  'script',
  'header',
  'blockquote',
  'code-block',
  'list',
  'indent',
  'align',
];

export function isDeltaEmpty(value: string): boolean {
  if (!value) return true;
  try {
    const delta = JSON.parse(value) as { ops?: Array<{ insert?: unknown }> };
    const text =
      delta.ops?.map((op) => (typeof op.insert === 'string' ? op.insert : ' ')).join('') ?? '';
    return text.trim() === '';
  } catch {
    return value.trim() === '';
  }
}

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  compact?: boolean;
  id?: string;
  /** Disables editing and hides the toolbar — a Quill-rendered (not DOMPurify-rendered) read-only display. */
  readOnly?: boolean;
  /** Maximum character count. Overflow typed past this cap is deleted in the `text-change` handler. */
  maxLength?: number;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter description…',
  compact = false,
  id,
  readOnly = false,
  maxLength,
}: RichTextEditorProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const maxLengthRef = useRef(maxLength);
  maxLengthRef.current = maxLength;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const q = new Quill(container, {
      theme: 'snow',
      placeholder,
      readOnly,
      formats: ALLOWED_FORMATS,
      modules: {
        toolbar: readOnly
          ? false
          : {
              container: compact ? COMPACT_TOOLBAR : STANDARD_TOOLBAR,
              handlers: {
                link(this: { quill: Quill }, val: boolean): void {
                  if (val) {
                    const url = window.prompt('Enter URL');
                    if (url && isAllowedUrl(url)) {
                      this.quill.format('link', url);
                    }
                  } else {
                    this.quill.format('link', false);
                  }
                },
              },
            },
        history: { delay: 1000, maxStack: 100, userOnly: true },
        clipboard: { matchVisual: false },
      },
    });

    quillRef.current = q;

    if (value) {
      try {
        q.setContents(JSON.parse(value), 'silent');
      } catch {
        q.setText(value, 'silent');
      }
    }

    q.on('text-change', (_delta, _old, source) => {
      if (source !== 'user') return;
      const cap = maxLengthRef.current;
      if (cap !== undefined) {
        const length = q.getLength() - 1; // exclude Quill's trailing newline
        if (length > cap) {
          q.deleteText(cap, length - cap, 'silent');
        }
      }
      onChangeRef.current(JSON.stringify(q.getContents()));
    });

    return (): void => {
      quillRef.current = null;
      // Remove ql-toolbar Quill injected as sibling before the container
      const toolbar = container.previousElementSibling;
      if (toolbar?.classList.contains('ql-toolbar')) {
        toolbar.remove();
      }
      // Reset the container so a remount (React StrictMode) gets a clean slate
      container.className = '';
      container.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    const q = quillRef.current;
    if (!q) return;
    const current = JSON.stringify(q.getContents());
    if (current !== value) {
      try {
        q.setContents(JSON.parse(value), 'silent');
      } catch {
        q.setText(value ?? '', 'silent');
      }
    }
  }, [value]);

  return (
    <div
      id={id}
      className="rich-text-editor"
      aria-label="Rich text editor"
      aria-readonly={readOnly || undefined}
    >
      <div ref={containerRef} />
    </div>
  );
}
